import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { finalize, map, mergeMap, startWith, withLatestFrom } from 'rxjs/operators';
import { CropVarietiesByCropId, SiteState } from 'src/app/shared/models';
import { CropVarietyService } from 'src/app/shared/services/crop-variety.service';

import {
  CropVarietyActionTypes,
  LoadCropVarietiesByCropIdRequest,
  LoadCropVarietiesByCropIdsRequest,
  LoadCropVarietiesByCropIdsSuccess,
  LoadCropVarietiesByCropIdSuccess,
} from '../actions/crop-variety.actions';
import { getCropVarietiesByCropId } from '../selectors/crop-variety.selectors';

@Injectable()
export class CropVarietyEffects {
  private readonly actions$ = inject(Actions);
  private readonly cropVarietyService = inject(CropVarietyService);
  private readonly store = inject<Store<SiteState>>(Store);

  loadCropVarietiesByCropId$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropVarietyActionTypes.LoadCropVarietiesByCropIdRequest),
      withLatestFrom(this.store.pipe(select(getCropVarietiesByCropId))),
      mergeMap(
        ([action, cropVarietiesByCropIds]: [
          LoadCropVarietiesByCropIdRequest,
          CropVarietiesByCropId[],
        ]) => {
          const cropVarietiesByCropIdInStore = cropVarietiesByCropIds.find(
            (x) => x.id === action.cropId,
          );
          return !cropVarietiesByCropIdInStore
            ? this.cropVarietyService.getCropVarietiesByCropId(action.cropId).pipe(
                map(
                  (cropVarieties) =>
                    new LoadCropVarietiesByCropIdSuccess(action.cropId, cropVarieties),
                ),
                startWith(CoreActions.setLoading({ loading: true })),
                finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
              )
            : of(new LoadCropVarietiesByCropIdSuccess(null, []));
        },
      ),
    ),
  );

  loadCropVarietiesByCropIds$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropVarietyActionTypes.LoadCropVarietiesByCropIdsRequest),
      withLatestFrom(this.store.pipe(select(getCropVarietiesByCropId))),
      mergeMap(
        ([action, cropVarietiesByCropIds]: [
          LoadCropVarietiesByCropIdsRequest,
          CropVarietiesByCropId[],
        ]) => {
          const cropIdsToLoad = action.cropIds.filter(
            (i) => !cropVarietiesByCropIds.map((x) => x.id).includes(i),
          );
          return cropIdsToLoad.length > 0
            ? this.cropVarietyService.getCropVarietiesByCropIds(cropIdsToLoad).pipe(
                map(
                  (cropVarieties) =>
                    new LoadCropVarietiesByCropIdsSuccess(cropIdsToLoad, cropVarieties),
                ),
                startWith(CoreActions.setLoading({ loading: true })),
                finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
              )
            : of(new LoadCropVarietiesByCropIdsSuccess([], []));
        },
      ),
    ),
  );
}
